import React, { useMemo, useState } from "react";
import {
  Alert, Box, Button, MenuItem, Stack, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import VoicemailIcon from "@mui/icons-material/Voicemail";
import {
  useGetCallsQuery, usePlaceCallMutation, useGetNumbersQuery,
  useGetVoicemailsQuery, useMarkVoicemailReadMutation,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, DataTable, StatusBadge, FilterBar, SearchBox,
  ActionButton, SlidePanel, type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const fmtDuration = (s: number) => {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};

const CallHistory: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState<{ direction?: string; status?: string }>({});
  const { data, isLoading, refetch } = useGetCallsQuery(filters);
  const { data: vmData, refetch: refetchVm } = useGetVoicemailsQuery();
  const { data: numbersData } = useGetNumbersQuery();
  const [placeCall, { isLoading: calling }] = usePlaceCallMutation();
  const [markRead] = useMarkVoicemailReadMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ number_id: "", to: "", customer_name: "" });
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [search, setSearch] = useState("");
  const [vmSearch, setVmSearch] = useState("");

  const rows = data?.data ?? [];
  const voicemails = useMemo(() => vmData?.data ?? [], [vmData]);
  const numbers = (numbersData?.data ?? []).filter((n: any) => n.voice_enabled);

  const filteredCalls = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c: any) =>
      [c.from_number, c.to_number, c.provider, c.status, c.direction]
        .filter(Boolean).some((v: string) => String(v).toLowerCase().includes(q)));
  }, [rows, search]);

  const filteredVm = useMemo(() => {
    const q = vmSearch.trim().toLowerCase();
    if (!q) return voicemails;
    return voicemails.filter((v: any) =>
      [v.from_number, v.transcription]
        .filter(Boolean).some((val: string) => String(val).toLowerCase().includes(q)));
  }, [voicemails, vmSearch]);

  const unreadCount = voicemails.filter((v: any) => !v.is_read).length;

  const dial = async () => {
    setResult(null);
    try {
      const res: any = await placeCall({
        number_id: form.number_id || undefined, to: form.to, customer_name: form.customer_name || undefined,
      }).unwrap();
      setResult({ ok: !!res.status, msg: res.status ? "Call initiated — your phone will ring." : (res.message || "Failed") });
      refetch();
    } catch (e: any) {
      setResult({ ok: false, msg: e?.data?.message || "Failed to place call" });
    }
  };

  const callColumns: DataTableColumn<any>[] = [
    {
      id: "created_at", label: "Date",
      render: (c) => (
        <Typography fontSize={13} color="text.secondary">
          {new Date(c.created_at).toLocaleString()}
        </Typography>
      ),
    },
    {
      id: "direction", label: "Direction",
      render: (c) => (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {c.direction === "inbound"
            ? <CallReceivedIcon sx={{ fontSize: 15, color: NAVY, opacity: 0.7 }} />
            : <CallMadeIcon sx={{ fontSize: 15, color: NAVY, opacity: 0.5 }} />}
          <Typography fontSize={13} fontWeight={600} color={NAVY} sx={{ textTransform: "capitalize" }}>
            {c.direction}
          </Typography>
        </Stack>
      ),
    },
    { id: "from_number", label: "From", render: (c) => c.from_number || "—" },
    { id: "to_number", label: "To", render: (c) => c.to_number || "—" },
    {
      id: "duration_seconds", label: "Duration",
      render: (c) => (
        <Typography fontSize={13}>{fmtDuration(c.duration_seconds)}</Typography>
      ),
    },
    {
      id: "status", label: "Status",
      render: (c) => <StatusBadge status={c.status} />,
    },
    {
      id: "recordings", label: "Recording",
      render: (c) =>
        c.recordings && c.recordings.length > 0
          ? <a href={c.recordings[0].url} target="_blank" rel="noreferrer">▶ Play</a>
          : <Typography fontSize={13} color="text.secondary">—</Typography>,
    },
    {
      id: "provider", label: "Provider",
      render: (c) => (
        <Typography fontSize={13} color="text.secondary">{c.provider || "—"}</Typography>
      ),
    },
  ];

  const vmColumns: DataTableColumn<any>[] = [
    {
      id: "created_at", label: "Date",
      render: (v) => (
        <Typography fontSize={13} color="text.secondary">
          {new Date(v.created_at).toLocaleString()}
        </Typography>
      ),
    },
    {
      id: "from_number", label: "From",
      render: (v) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <VoicemailIcon fontSize="small" color={v.is_read ? "disabled" : "secondary"} />
          <Typography fontSize={13} fontWeight={v.is_read ? 400 : 700}>{v.from_number}</Typography>
        </Stack>
      ),
    },
    {
      id: "duration_seconds", label: "Duration",
      render: (v) => <Typography fontSize={13}>{fmtDuration(v.duration_seconds)}</Typography>,
    },
    {
      id: "transcription", label: "Transcription",
      render: (v) => (
        <Box sx={{ maxWidth: 320 }}>
          <Typography fontSize={13}>{v.transcription || "—"}</Typography>
        </Box>
      ),
    },
    {
      id: "recording_url", label: "Recording",
      render: (v) =>
        v.recording_url
          ? <a href={v.recording_url} target="_blank" rel="noreferrer">▶ Play</a>
          : <Typography fontSize={13} color="text.secondary">—</Typography>,
    },
    {
      id: "actions_read", label: "",
      render: (v) =>
        !v.is_read ? (
          <Button
            size="small"
            onClick={async (e) => { e.stopPropagation(); await markRead(v.id); refetchVm(); }}
          >
            Mark read
          </Button>
        ) : null,
    },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<CallRoundedIcon />}
          title="Voice"
          subtitle="Call history and voicemail inbox"
          actions={
            <ActionButton
              startIcon={<CallRoundedIcon />}
              onClick={() => { setResult(null); setOpen(true); }}
            >
              Click to Call
            </ActionButton>
          }
        />
      }
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Tab label="Call History" />
        <Tab
          label={
            voicemails.length
              ? `Voicemail${unreadCount ? ` (${unreadCount})` : ""}`
              : "Voicemail"
          }
        />
      </Tabs>

      {tab === 0 && (
        <>
          <FilterBar
            right={
              <Typography fontSize={13} color="text.secondary">
                {filteredCalls.length} call{filteredCalls.length !== 1 ? "s" : ""}
              </Typography>
            }
          >
            <SearchBox value={search} onChange={setSearch} placeholder="Search calls…" />
            <TextField
              select size="small" label="Direction" sx={{ width: 160 }}
              value={filters.direction ?? ""}
              onChange={(e) => setFilters({ ...filters, direction: e.target.value || undefined })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="outbound">Outbound</MenuItem>
              <MenuItem value="inbound">Inbound</MenuItem>
            </TextField>
            <TextField
              select size="small" label="Status" sx={{ width: 160 }}
              value={filters.status ?? ""}
              onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
            >
              <MenuItem value="">All</MenuItem>
              {["queued", "ringing", "in_progress", "completed", "missed", "voicemail", "failed"].map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </FilterBar>

          <DataTable
            columns={callColumns}
            rows={filteredCalls}
            loading={isLoading}
            empty={{
              icon: <CallRoundedIcon />,
              title: "No calls yet",
              message: "Call records will appear here once your numbers make or receive calls.",
            }}
          />
        </>
      )}

      {tab === 1 && (
        <>
          <FilterBar
            right={
              <Typography fontSize={13} color="text.secondary">
                {filteredVm.length} voicemail{filteredVm.length !== 1 ? "s" : ""}
                {unreadCount > 0 && ` · ${unreadCount} unread`}
              </Typography>
            }
          >
            <SearchBox value={vmSearch} onChange={setVmSearch} placeholder="Search voicemails…" />
          </FilterBar>

          <DataTable
            columns={vmColumns}
            rows={filteredVm}
            getRowKey={(v) => v.id}
            empty={{
              icon: <VoicemailIcon />,
              title: "No voicemails",
              message: "Voicemail recordings will appear here.",
            }}
          />
        </>
      )}

      <SlidePanel
        open={open}
        onClose={() => setOpen(false)}
        title="Click to Call"
        subtitle="Your extension rings first, then the customer is dialed"
        icon={<CallRoundedIcon />}
        footer={
          <>
            <Button variant="outlined" onClick={() => setOpen(false)} sx={{ borderRadius: "10px" }}>
              Close
            </Button>
            <ActionButton onClick={dial} disabled={!form.to || calling}>
              {calling ? "Dialing…" : "Call"}
            </ActionButton>
          </>
        }
      >
        <Stack spacing={2}>
          {result && <Alert severity={result.ok ? "success" : "error"}>{result.msg}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Your configured voice extension rings first, then the customer is dialed.
          </Typography>
          <TextField
            select label="From Number" value={form.number_id}
            onChange={(e) => setForm({ ...form, number_id: e.target.value })} fullWidth
          >
            <MenuItem value="">Auto (first voice number)</MenuItem>
            {numbers.map((n: any) => (
              <MenuItem key={n.id} value={n.id}>{n.number} ({n.voice_provider})</MenuItem>
            ))}
          </TextField>
          <TextField
            label="To (E.164, e.g. +14165551234)" value={form.to}
            onChange={(e) => setForm({ ...form, to: e.target.value })} fullWidth required
          />
          <TextField
            label="Customer name (optional)" value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })} fullWidth
          />
        </Stack>
      </SlidePanel>
    </AppPage>
  );
};

export default CallHistory;
